import type {
    CameraPlugin,
    ImageOptions,
    Photo,
    GalleryPhotos,
    GalleryImageOptions,
    CameraPermissionStatus,
} from './definitions';
import { HuaweiBridgeClient } from './huawei-bridge-client';

/**
 * CameraHuawei implements the same CameraPlugin interface as
 * @capacitor/camera, routing calls through the Huawei JS Bridge.
 *
 * Backed by HarmonyOS system pickers (no permissions required):
 * - CAMERA source: secure system camera (cameraPicker)
 * - PHOTOS source: system gallery (PhotoViewPicker)
 * - PROMPT source: ActionSheet letting the user choose
 *
 * User cancellation rejects with Error('User cancelled'), matching
 * the Capacitor behavior.
 */
export class CameraHuawei implements CameraPlugin {
    private bridge: HuaweiBridgeClient;

    constructor() {
        this.bridge = HuaweiBridgeClient.getInstance();
    }

    async getPhoto(options: ImageOptions): Promise<Photo> {
        const params: Record<string, any> = {
            resultType: options.resultType,
        };
        if (options.source !== undefined) {
            params.source = options.source;
        }
        if (options.direction !== undefined) {
            params.direction = options.direction;
        }
        if (options.quality !== undefined) {
            params.quality = options.quality;
        }
        if (options.width !== undefined) {
            params.width = options.width;
        }
        if (options.height !== undefined) {
            params.height = options.height;
        }
        if (options.promptLabelHeader !== undefined) {
            params.promptLabelHeader = options.promptLabelHeader;
        }
        if (options.promptLabelPhoto !== undefined) {
            params.promptLabelPhoto = options.promptLabelPhoto;
        }
        if (options.promptLabelPicture !== undefined) {
            params.promptLabelPicture = options.promptLabelPicture;
        }

        const res = await this.bridge.call('cameraGetPhoto', params);
        const photo: Photo = {
            format: res && res.format ? res.format : 'jpeg',
            saved: !!(res && res.saved),
        };
        if (res && res.base64String !== undefined) {
            photo.base64String = res.base64String;
        }
        if (res && res.dataUrl !== undefined) {
            photo.dataUrl = res.dataUrl;
        }
        if (res && res.path !== undefined) {
            photo.path = res.path;
        }
        if (res && res.webPath !== undefined) {
            photo.webPath = res.webPath;
        }
        return photo;
    }

    async pickImages(options?: GalleryImageOptions): Promise<GalleryPhotos> {
        const params: Record<string, any> = {};
        if (options && options.limit !== undefined) {
            params.limit = options.limit;
        }
        const res = await this.bridge.call('cameraPickImages', params);
        const photos = res && Array.isArray(res.photos) ? res.photos : [];
        return { photos };
    }

    /**
     * The system pickers need no permissions, so this always reports
     * 'granted' for both camera and photos.
     */
    async checkPermissions(): Promise<CameraPermissionStatus> {
        return this.bridge.call('checkCameraPermissions');
    }

    /**
     * The system pickers need no permissions, so this always reports
     * 'granted' for both camera and photos (request is a no-op).
     */
    async requestPermissions(): Promise<CameraPermissionStatus> {
        return this.bridge.call('requestCameraPermissions');
    }
}
